import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../0_context/AppContext.js';
import { BodyInfoSection } from './section/BodyinfoSection.js';
import { SupplementsSection } from './section/SupplementsSection.js';
import { RecentRecordsSection } from './section/RecentRecordSection.js';
import { CardioBlock } from './section/block/CardioBlock.js';
import { WorkoutBlock } from './section/block/WorkoutBlock.js';

export default function RecordPage({ isActive }) {
    const {
        workDate, setWorkDate,
        exerciseList = [], cardioList = [],
        workouts = [], cardioWorkouts = [],
        itemsPerPage = 5, currentPage = 1, cardioCurrentPage = 1,
        deleteRecord, deleteCardioRecord,
        supplementsList = [], deleteSupplements, handleSupplementSave,
        saveAll, getRecentMaxText, authStatus,
        loadTempDraft, saveTempDraft, clearTempDraft,
    } = useAppContext();

    const [draftBlocks, setDraftBlocks] = useState([]);
    const [supplementText, setSupplementText] = useState('');
    const [supplementDate, setSupplementDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const [hasTempDraft, setHasTempDraft] = useState(false);

    // 데이터 배열 보장 및 방어적 검사
    const safeWorkouts = Array.isArray(workouts) ? workouts : [];
    const safeCardioWorkouts = Array.isArray(cardioWorkouts) ? cardioWorkouts : [];
    const safeSupplementsList = Array.isArray(supplementsList) ? supplementsList : [];
    
    const hasCompletedTodayWorkout = safeWorkouts.some(w => w?.date === workDate) || safeCardioWorkouts.some(c => c?.date === workDate);
    
    useEffect(() => {
        const handleViewportChange = () => {
            if (window.visualViewport) {
                const keyboardHeight = window.innerHeight - window.visualViewport.height;
                setIsKeyboardOpen(keyboardHeight > 150);
            }
        };

        const handleFocusIn = e => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName)) {
                setTimeout(handleViewportChange, 100);
            }
        };

        const handleFocusOut = () => setTimeout(handleViewportChange, 100);

        const vv = window.visualViewport;
        if (vv) {
            vv.addEventListener('resize', handleViewportChange);
            vv.addEventListener('scroll', handleViewportChange);
        }
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);

        return () => {
            if (vv) {
                vv.removeEventListener('resize', handleViewportChange);
                vv.removeEventListener('scroll', handleViewportChange);
            }
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
        };
    }, []);

    useEffect(() => {
        const stored = localStorage.getItem('logup_temp_draft');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setHasTempDraft(parsed.date === workDate && Array.isArray(parsed.draftBlocks) && parsed.draftBlocks.length > 0);
            } catch (e) {
                setHasTempDraft(false);
            }
        } else {
            setHasTempDraft(false);
        }
    }, [workDate]);

    if (!isActive) return null;
    const isLoggedIn = authStatus === 'google';
    const phaseOptions = ['준비운동', '본 운동', '마무리'];
    const difficultyOptions = ['쉬움', '보통', '어려움'];

    // 정렬 및 페이징 시 배열 전개 연산자 방어 코드 적용
    const sortedWorkouts = [...safeWorkouts].sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
    const activeHistoryPageData = sortedWorkouts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const sortedCardioWorkouts = [...safeCardioWorkouts].sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
    const activeCardioHistoryPageData = sortedCardioWorkouts.slice((cardioCurrentPage - 1) * itemsPerPage, cardioCurrentPage * itemsPerPage);

    const handleSaveDraft = newBlocks => {
        const safeBlocks = Array.isArray(newBlocks) ? newBlocks : [];
        setDraftBlocks(safeBlocks);
        saveTempDraft(safeBlocks, workDate);
    };

    const addCardioBlock = () => {
        const safeCardioList = Array.isArray(cardioList) ? cardioList : [];
        handleSaveDraft([...draftBlocks, {
            id: Date.now(),
            type: 'cardio',
            name: safeCardioList.length > 0 ? safeCardioList[0].name : '',
            sets: [{ time: '', distance: '', reps: '', speed: '', incline: '', phase: '본 운동' }]
        }]);
    };

    const addWorkoutBlock = () => {
        const safeExerciseList = Array.isArray(exerciseList) ? exerciseList : [];
        handleSaveDraft([...draftBlocks, {
            id: Date.now(),
            type: 'workout',
            name: safeExerciseList.length > 0 ? safeExerciseList[0] : '',
            sets: [{ weight: '', reps: '', difficulty: '보통' }] 
        }]);
    };

    const updateBlock = (blockId, updatedFields) => {
        handleSaveDraft(draftBlocks.map(block => block.id === blockId ? { ...block, ...updatedFields } : block));
    };

    const removeBlock = (blockId) => {
        handleSaveDraft(draftBlocks.filter(block => block.id !== blockId));
    };

    const handleRestoreDraft = () => {
        const restoredDrafts = loadTempDraft(workDate);
        if (Array.isArray(restoredDrafts) && restoredDrafts.length > 0) {
            setDraftBlocks(restoredDrafts);
            setHasTempDraft(false);
            alert("작성 중이던 운동 기록과 종목 정보가 안전하게 불러와졌습니다.");
        } else {
            alert("불러올 임시 저장 기록이 없거나 다른 날짜의 기록입니다.");
        }
    };

    const handleSaveAll = () => {
        const hasWorkouts = draftBlocks.length > 0;
        const hasSupplements = supplementText && supplementText.trim().length > 0;
        
        if (!hasWorkouts && !hasSupplements) {
            alert("저장할 운동이나 영양/보조제 기록이 없습니다.");
            return;
        }
        if (hasWorkouts) {
            const blocksToSave = draftBlocks.map(block => {
                if (block.type === 'workout') {
                    return {
                        ...block,
                        sets: (block.sets || []).map(set => ({ ...set, difficulty: set.difficulty || '보통'}))
                    };
                }
                return block;
            });
            if (typeof saveAll === 'function') saveAll(blocksToSave, workDate);
            clearTempDraft();
            setDraftBlocks([]);
            setHasTempDraft(false);
        }

        if (hasSupplements) {
            if (typeof handleSupplementSave === 'function') {
                handleSupplementSave({ supplementText, supplementDate });
            }
            setSupplementText('');
        }
        alert("오늘의 기록이 모두 저장되었습니다!");
    };

    return (
        <div className="text-left pb-24">
            <div className={`max-w-md mx-auto fixed ${isKeyboardOpen ? 'bottom-0 shadow-lg border-t' : 'bottom-20'} w-full bg-white/90 backdrop-blur-md border-b border-slate-100 z-50 px-4 py-3 flex gap-3 print-hide transition-all duration-150`}>
                <button onClick={addCardioBlock} className="flex-1 py-3 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-xl text-sm font-black transition-colors flex items-center justify-center gap-1 border border-teal-200">
                    <span className="material-icons-round text-sm">directions_run</span> 유산소 추가
                </button>
                <button onClick={addWorkoutBlock} className="flex-1 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl text-sm font-black transition-colors flex items-center justify-center gap-1 border border-indigo-200">
                    <span className="material-icons-round text-sm">fitness_center</span> 헬스 추가
                </button>
            </div>

            <div className="pt-8 space-y-8 animate-fadeIn">
                {/* 00. 사용자 신체 정보 */}
                <BodyInfoSection />

                {/* 01. 사용자가 추가하는 운동 기록 섹션 */}
                <section className='ml-4 mr-4'>
                    <div className="flex items-center gap-2 mb-4 px-1">
                        <span className="bg-slate-800 text-white text-[10px] font-black px-2 py-0.5 rounded-md tracking-wider">01</span>
                        <h3 className="text-lg font-black text-slate-800 tracking-tight">운동</h3>
                    </div>

                    <div className="flex gap-3 mb-5">
                        <input 
                            type="date" 
                            value={workDate} 
                            onChange={(e) => setWorkDate(e.target.value)} 
                            className="flex-1 bg-white text-slate-700 text-sm font-bold rounded-2xl px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-slate-500 outline-none transition-all shadow-sm"
                        />
                    </div>

                    <div className="space-y-6">
                        {draftBlocks.length === 0 && (
                            <div className="text-center py-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-300">
                                {hasTempDraft ? (
                                    <div className="py-60 flex flex-col items-center justify-center space-y-4 animate-fadeIn">
                                        <div className="w-12 h-12 bg-indigo-100 text-indigo-500 rounded-full flex items-center justify-center">
                                            <span className="material-icons-round">restore_page</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-slate-700">작성 중이던 운동 기록이 있습니다.</p>
                                            <p className="text-[11px] text-slate-400 mt-1">앱이 종료되어 저장되지 않은 기록을 복구할까요?</p>
                                        </div>
                                        <button 
                                            onClick={handleRestoreDraft}
                                            className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm font-black transition-colors flex items-center gap-2 shadow-sm"
                                        >
                                            <span className="material-icons-round text-[18px]">restore</span>
                                            이어서 작성하기
                                        </button>
                                    </div>
                                ) : hasCompletedTodayWorkout ? (
                                    <div className='py-60'>
                                        <p className="text-sm font-bold text-indigo-600 mb-2">오늘 운동 기록을 완료했습니다.</p>
                                        <p className="text-xs text-slate-600">새롭게 저장하여 덮어씌워집니다.</p>
                                    </div>
                                ) : (
                                    <div className='py-60'>
                                        <p className="text-sm font-bold text-red-600 mb-2">현재 진행 중 인 운동이 없습니다.</p>
                                        <p className="text-xs text-slate-600">하단의 유산소 또는 헬스를 추가해주세요.</p>
                                        <p className="text-xs text-slate-400">자유롭게 추가하여 저장 할 수 있습니다.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {draftBlocks.map((block) => {
                            if (block.type === 'cardio') return (<CardioBlock key={block.id} block={block} updateBlock={updateBlock} cardioList={cardioList} removeBlock={removeBlock} phaseOptions={phaseOptions} />);                    
                            if (block.type === 'workout') return (<WorkoutBlock key={block.id} block={block} removeBlock={removeBlock} updateBlock={updateBlock} exerciseList={exerciseList} getRecentMaxText={getRecentMaxText} difficultyOptions={difficultyOptions} />);
                            return null;
                        })}
                    </div>
                </section>

                {/* 02. 보조제 복용 기록 */}
                <SupplementsSection 
                    supplementHistoryData={safeSupplementsList}
                    isLoggedIn={isLoggedIn}
                    handleSupplementSave={handleSupplementSave}
                    supplementText={supplementText}
                    setSupplementText={setSupplementText}
                    supplementDate={supplementDate}
                    setSupplementDate={setSupplementDate}
                />

                {/* 03. 최근 기록 컴포넌트 */}
                <RecentRecordsSection 
                    activeCardioHistoryPageData={activeCardioHistoryPageData}
                    deleteCardioRecord={deleteCardioRecord}
                    activeHistoryPageData={activeHistoryPageData}
                    deleteRecord={deleteRecord}
                    supplementHistoryData={safeSupplementsList}
                    deleteSupplements={deleteSupplements}
                />
                
                <div className="mx-4 mt-2 mb-12">
                    <button onClick={handleSaveAll} className="w-full py-5 bg-slate-800 hover:bg-slate-900 text-white rounded-3xl text-lg font-black shadow-xl shadow-slate-300 transition-all active:scale-95 flex items-center justify-center gap-2">
                        <span className="material-icons-round">save</span>
                        오늘 운동 저장
                    </button>
                </div>
            </div>
        </div>
    );
}